import * as React from 'react';
import {
  List,
  TextField,
  FunctionField,
  Button,
  Link,
  useDataProvider,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Typography,
  Box,
  Chip,
  Divider,
  Stack,
  TextField as MuiTextField,
} from '@mui/material';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import EmptyList from '../components/misc/EmptyList';
import ResizableDatagrid from '../components/table/ResizableDatagrid';
import { getSaleById } from '../services/salesService';
import { refundPayment } from '../services/paymentsService';
import { logRefundView, manualCloseRefundRequest } from '../services/pendingRefundsService';

const entity = 'refundRequests';

/** Onay akışı yok: waiting → (scheduled) → completed | failed | closed */
const statusLabel = (status) => {
  switch (status) {
    case 'waiting':
    case 'pending':
      return { label: 'İade bekliyor', color: 'warning' };
    case 'scheduled':
      return { label: 'Planlandı', color: 'info' };
    case 'done':
    case 'completed':
      return { label: 'Tamamlandı', color: 'success' };
    case 'failed':
      return { label: 'Başarısız', color: 'error' };
    case 'closed':
      return { label: 'Manuel kapatıldı', color: 'default' };
    default:
      return { label: status || '—', color: 'default' };
  }
};

const ACTIONABLE_STATUSES = new Set(['failed', 'scheduled']);

const actionLabel = (action) => {
  switch (action) {
    case 'started':
      return 'İade başlatıldı';
    case 'scheduled':
      return 'Ertesi güne planlandı';
    case 'processed':
      return 'İade işlendi';
    case 'failed':
      return 'İade hatası';
    case 'manual_closed':
      return 'Manuel kapatıldı';
    case 'retried':
      return 'Tekrar denendi';
    case 'viewed':
      return 'Görüntülendi';
    default:
      return action || '—';
  }
};

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** sale.pendingRefund → POST /payments/:id/refund gövdesi */
const buildRetryBody = (sale, record) => {
  const pr = sale?.pendingRefund || sale?.extras?.pendingRefund || null;
  const reason = pr?.reason || record?.reason || 'İade tekrar denendi (panel)';
  if (pr && !pr.isFull && Array.isArray(pr.items) && pr.items.length) {
    return {
      items: pr.items.map((it) => ({
        groupKey: it.groupKey,
        productId: it.productId,
        count: it.count,
      })),
      reason,
    };
  }
  if (pr?.isFull) return { reason };
  return null;
};

const RefundRowActions = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const busyRef = React.useRef(false);
  const [busy, setBusy] = React.useState(false);
  const [closeOpen, setCloseOpen] = React.useState(false);
  const [note, setNote] = React.useState('');
  const idempotencyKeyRef = React.useRef(newIdempotencyKey());

  if (permissions?.crole !== 'admin') return null;
  if (!record || !ACTIONABLE_STATUSES.has(record.status)) return null;

  const handleRetry = async () => {
    if (busyRef.current) return;
    if (!window.confirm('Bu iade bankaya tekrar gönderilsin mi?')) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const sale = record.saleId ? await getSaleById(record.saleId) : null;
      const paymentRef = sale?.payment;
      const paymentId =
        record.paymentId || (typeof paymentRef === 'string' ? paymentRef : paymentRef?.id) || null;
      if (!paymentId) throw new Error('Ödeme kaydı bulunamadı');
      const body = buildRetryBody(sale, record);
      if (!body) {
        throw new Error(
          'Tekrar denenecek iade kalemi bulunamadı; ödeme sayfasından manuel iade yapın.',
        );
      }
      await refundPayment({
        paymentId,
        items: body.items,
        reason: body.reason,
        idempotencyKey: idempotencyKeyRef.current,
      });
      notify('İade tekrar denendi.', { type: 'success' });
      idempotencyKeyRef.current = newIdempotencyKey();
      refresh();
    } catch (err) {
      notify(err?.message || 'İade tekrar denenemedi', { type: 'error' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleManualClose = async () => {
    if (busyRef.current) return;
    const trimmed = note.trim();
    if (trimmed.length < 3) {
      notify('Not zorunludur', { type: 'warning' });
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      await manualCloseRefundRequest(record.id, trimmed);
      notify('Talep manuel kapatıldı.', { type: 'success' });
      setCloseOpen(false);
      setNote('');
      refresh();
    } catch (err) {
      notify(err?.message || 'Manuel kapatma başarısız', { type: 'error' });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={0.5}>
        <MuiButton size="small" variant="outlined" onClick={handleRetry} disabled={busy}>
          Tekrar dene
        </MuiButton>
        <MuiButton size="small" color="inherit" onClick={() => setCloseOpen(true)} disabled={busy}>
          Manuel kapat
        </MuiButton>
      </Stack>
      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Talebi manuel kapat</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Banka iadesi yapılmadan talep kapatılır. Neden kapattığınızı yazın.
          </Typography>
          <MuiTextField
            label="Not"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCloseOpen(false)} disabled={busy}>
            Vazgeç
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleManualClose}
            disabled={busy || note.trim().length < 3}
          >
            Kapat
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RefundDetailDialog = ({ open, onClose, record }) => {
  if (!record) return null;
  const r = record;
  const logs = Array.isArray(r.logs) ? r.logs : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>İade Detayı</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Typography>
            <strong>Ad Soyad:</strong> {r.userName} {r.userSurname}
          </Typography>
          <Typography>
            <strong>E-posta:</strong> {r.userEmail || '—'}
          </Typography>
          <Typography>
            <strong>Etkinlik:</strong> {r.eventName || '—'}
          </Typography>
          <Typography>
            <strong>Ürün / Bilet:</strong> {r.productInfo || '—'}
          </Typography>
          <Typography>
            <strong>PNR:</strong> {r.ticketPnr || '—'}
          </Typography>
          <Typography>
            <strong>Tutar:</strong> {r.price != null ? `${r.price} TL` : '—'}
          </Typography>
          <Typography>
            <strong>Bilet alım tarihi:</strong>{' '}
            {r.ticketCreated ? moment(r.ticketCreated).format('LLLL') : '—'}
          </Typography>
          <Typography>
            <strong>Etkinlik tarihi:</strong>{' '}
            {r.eventSession ? moment(r.eventSession).format('LLLL') : '—'}
          </Typography>
          <Typography>
            <strong>Talep tarihi:</strong>{' '}
            {r.createdate ? moment(r.createdate).format('LLLL') : '—'}
          </Typography>
          <Typography>
            <strong>Talep eden:</strong> {r.createdby || r.userEmail || '—'}
          </Typography>
          <Typography>
            <strong>Durum:</strong> {statusLabel(r.status).label}
          </Typography>
          {r.processedBy && (
            <Typography>
              <strong>İade eden:</strong> {r.processedBy}
              {r.processedAt ? ` — ${moment(r.processedAt).format('LLLL')}` : ''}
            </Typography>
          )}
          {r.lastError && (
            <Typography color="error">
              <strong>Son hata:</strong> {r.lastError}
            </Typography>
          )}
          {r.reason && (
            <Typography>
              <strong>Not:</strong> {r.reason}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">İşlem geçmişi</Typography>
          {logs.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              Log yok
            </Typography>
          ) : (
            [...logs].reverse().map((entry, idx) => (
              <Box
                key={`${entry.at}-${idx}`}
                sx={{
                  p: 1,
                  bgcolor: '#fafafa',
                  borderRadius: 1,
                  border: '1px solid #eee',
                }}
              >
                <Typography variant="body2">
                  <strong>{actionLabel(entry.action)}</strong>
                  {' — '}
                  {entry.at ? moment(entry.at).format('LLL') : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Kim: {entry.actor || '—'}
                </Typography>
                {entry.detail && <Typography variant="body2">{entry.detail}</Typography>}
                {entry.error && (
                  <Typography variant="body2" color="error">
                    Hata: {entry.error}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Kapat</MuiButton>
      </DialogActions>
    </Dialog>
  );
};

const DetailButton = ({ record, autoOpen }) => {
  const translate = useTranslate();
  const [open, setOpen] = React.useState(Boolean(autoOpen));
  const [liveRecord, setLiveRecord] = React.useState(record);
  const dataProvider = useDataProvider();

  React.useEffect(() => {
    setLiveRecord(record);
  }, [record]);

  React.useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  React.useEffect(() => {
    if (!open || !record?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await logRefundView({
          refundRequestId: record.id,
          paymentId: record.paymentId || undefined,
          saleId: record.saleId || undefined,
          source: 'detail',
        });
        const refreshed = await dataProvider.getOne('refundRequests', {
          id: record.id,
        });
        if (!cancelled && refreshed?.data) setLiveRecord(refreshed.data);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, record?.id, record?.paymentId, record?.saleId, dataProvider]);

  if (!record) return null;
  return (
    <>
      <Button
        label={translate('refund.view_detail', { _: 'Detay Gör' })}
        onClick={() => setOpen(true)}
      />
      <RefundDetailDialog
        open={open}
        onClose={() => setOpen(false)}
        record={liveRecord || record}
      />
    </>
  );
};

const resolvePaymentId = (record) => {
  if (!record) return null;
  if (record.paymentId) return String(record.paymentId);
  const salePayment = record.body?.ticket?.sale;
  if (typeof salePayment === 'object' && salePayment?.payment) {
    const p = salePayment.payment;
    return typeof p === 'string' ? p : (p?.id ?? null);
  }
  return null;
};

const ViewRefundButton = ({ record }) => {
  const translate = useTranslate();
  const [paymentId, setPaymentId] = React.useState(() => resolvePaymentId(record));
  const [loading, setLoading] = React.useState(!resolvePaymentId(record) && !!record?.saleId);

  React.useEffect(() => {
    if (!record) return undefined;
    const direct = resolvePaymentId(record);
    if (direct) {
      setPaymentId(direct);
      setLoading(false);
      return undefined;
    }
    if (!record.saleId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const sale = await getSaleById(record.saleId);
        const payment = sale?.payment;
        const id = typeof payment === 'string' ? payment : payment?.id;
        if (!cancelled && id) setPaymentId(String(id));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [record]);

  if (!record) return null;
  if (loading) return <span style={{ color: '#999' }}>...</span>;
  if (!paymentId) return <span style={{ color: '#999' }}>—</span>;

  return (
    <Button
      label={translate('refund.view_refund', { _: 'İadeyi Gör' })}
      component={Link}
      to={`/payments/${paymentId}/show`}
    />
  );
};

export const RefundRequestList = (props) => {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      exporter={false}
      sort={{ field: 'createdate', order: 'DESC' }}
      perPage={50}
      title={translate('resources.refundRequests.name', { _: 'İade Talepleri' })}
      filter={{ source: 'customer' }}
    >
      <ResizableDatagrid
        storageKey="gk-datagrid-widths:refundRequests"
        bulkActionButtons={false}
        rowStyle={(record) =>
          highlightId && record?.id === highlightId
            ? { backgroundColor: 'rgba(237,108,2,0.12)' }
            : undefined
        }
      >
        <FunctionField
          label={translate('resources.refundRequests.fields.status', { _: 'Durum' })}
          render={(r) => {
            const s = statusLabel(r.status);
            return <Chip size="small" label={s.label} color={s.color} />;
          }}
        />
        <FunctionField
          label={translate('resources.refundRequests.fields.user', { _: 'Kullanıcı' })}
          render={(r) => `${r.userName || ''} ${r.userSurname || ''}`.trim() || '—'}
        />
        <TextField
          source="userEmail"
          label={translate('resources.refundRequests.fields.userEmail', { _: 'E-posta' })}
        />
        <TextField
          source="eventName"
          label={translate('resources.refundRequests.fields.eventName', { _: 'Etkinlik' })}
        />
        <TextField
          source="productInfo"
          label={translate('resources.refundRequests.fields.productInfo', { _: 'Bilet / Ürün' })}
        />
        <TextField
          source="ticketPnr"
          label={translate('resources.refundRequests.fields.ticketPnr', { _: 'PNR' })}
        />
        <FunctionField
          label={translate('resources.refundRequests.fields.createdate', { _: 'İade Tarihi' })}
          render={(r) => (r.createdate ? moment(r.createdate).format('LLL') : '—')}
        />
        <FunctionField
          label={translate('resources.refundRequests.fields.processedBy', { _: 'İade eden' })}
          render={(r) => r.processedBy || '—'}
        />
        <FunctionField
          label=""
          render={(r) => (
            <DetailButton record={r} autoOpen={Boolean(highlightId && r.id === highlightId)} />
          )}
        />
        <FunctionField label="" render={(r) => <ViewRefundButton record={r} />} />
        <FunctionField label="" render={(r) => <RefundRowActions record={r} />} />
      </ResizableDatagrid>
    </List>
  );
};
